/**
 * AI Chat conversation surface controller.
 *
 * Owns only DOM reconciliation, scroll following and local loading placeholders.
 * Session/message persistence and rich message node creation remain injected so this
 * module never depends on stores, facades, or transport details directly.
 */
export function createConversationViewController({
  stream,
  newMessageButton,
  roleCards,
  getMessages,
  getCurrentSession,
  renderMessageContent,
  setInputVisibility,
  documentRef = document,
  setTimeoutFn = setTimeout,
}) {
  let autoScroll = true;
  let bound = false;
  const scrollBottomThreshold = 32;

  function isStreamAtBottom() {
    return stream.scrollHeight - stream.scrollTop - stream.clientHeight < scrollBottomThreshold;
  }

  function showNewMessageButton() {
    if (newMessageButton) newMessageButton.hidden = false;
  }

  function hideNewMessageButton() {
    if (newMessageButton) newMessageButton.hidden = true;
  }

  function resetScrollState() {
    autoScroll = true;
    hideNewMessageButton();
  }

  function createConversationEmptyState() {
    const empty = documentRef.createElement('div');
    empty.className = 'aichat-conversation-empty';
    empty.append(
      Object.assign(documentRef.createElement('strong'), { textContent: '会话已就绪' }),
      Object.assign(documentRef.createElement('div'), { textContent: '描述你的目标，或直接提供 ASIN、Listing 文案和需要分析的资料。Agent 会自动选择合适的工具与技能。' }),
    );
    return empty;
  }

  function createSessionLoadingLine(width, height, delay = '') {
    const line = documentRef.createElement('div');
    const widthClass = { '50%': '50', '60%': '60', '75%': '75', '80%': '80', '90%': '90' }[width] || '100';
    const delayClass = { '.15s': 'delay-15', '.3s': 'delay-30' }[delay] || '';
    line.className = `aichat-session-skeleton-line aichat-session-skeleton-line--${height} aichat-session-skeleton-line--${widthClass}${delayClass ? ` aichat-session-skeleton-line--${delayClass}` : ''}`;
    return line;
  }

  function createSessionLoadingSkeleton() {
    const loading = documentRef.createElement('div');
    loading.className = 'aichat-session-loading';
    [[false, [['60%', '14'], ['90%', '12', '.15s'], ['75%', '12', '.3s']]], [true, [['50%', '14'], ['80%', '12', '.15s']]]]
      .forEach(([isUser, lines]) => {
        const row = documentRef.createElement('div');
        row.className = `aichat-session-skeleton-row${isUser ? ' aichat-session-skeleton-row--user' : ''}`;
        const copy = documentRef.createElement('div');
        copy.className = `aichat-session-skeleton-copy${isUser ? ' aichat-session-skeleton-copy--user' : ''}`;
        lines.forEach(([width, height, delay]) => copy.append(createSessionLoadingLine(width, height, delay)));
        row.append(documentRef.createElement('div'), copy);
        row.firstElementChild.className = 'aichat-session-skeleton-avatar';
        loading.append(row);
      });
    return loading;
  }

  function showSessionLoading() {
    if (roleCards) roleCards.hidden = true;
    if (stream) stream.hidden = false;
    stream.replaceChildren(createSessionLoadingSkeleton());
    setInputVisibility(false);
  }

  // Diff-based rendering avoids rebuilding a streaming conversation on each token.
  function renderMessages() {
    if (!stream) return;
    const messages = getMessages();

    for (const element of Array.from(stream.children)) {
      if (!element.dataset.msgKey) element.remove();
    }

    const existingByKey = new Map();
    for (const element of Array.from(stream.children)) {
      const key = element.dataset.msgKey;
      if (key) existingByKey.set(key, element);
    }

    let previousElement = null;
    const seenKeys = new Set();
    let newMessageAdded = false;
    for (const message of messages) {
      const key = String(message.id ?? `tmp-${Math.random()}`);
      seenKeys.add(key);
      let element = existingByKey.get(key);
      const agentSignature = `${message.agentState ?? ''}|${(message.agentSkills || []).length}|${(message.agentMemoryLoad || []).length}|${message.agentQuality?.status ?? ''}|${message.agentQuality?.score ?? ''}|${(message.agentTimeline || []).map((step) => `${step.key}:${step.summary}`).join(';')}`;
      const signature = `${message.typing ? 'T' : 'F'}|${message.latencyMs ?? ''}|${message.finishReason ?? ''}|${message.content ?? ''}|${agentSignature}`;

      if (!element) {
        element = documentRef.createElement('div');
        const isUser = message.role === 'USER';
        element.className = `aichat-msg-group ${isUser ? 'aichat-msg-group--user' : 'aichat-msg-group--assistant'} aichat-msg-group--enter`;
        element.dataset.msgKey = key;
        renderMessageContent(element, message);
        element.dataset.sig = signature;
        if (previousElement) previousElement.after(element);
        else stream.prepend(element);
        setTimeoutFn(() => element.classList.remove('aichat-msg-group--enter'), 350);
        newMessageAdded = true;
      } else {
        if (element.dataset.sig !== signature) {
          renderMessageContent(element, message);
          element.dataset.sig = signature;
        }
        if (previousElement && previousElement.nextElementSibling !== element) previousElement.after(element);
      }
      previousElement = element;
    }

    for (const [key, element] of existingByKey) {
      if (!seenKeys.has(key)) element.remove();
    }

    if (messages.length === 0 && getCurrentSession()) stream.appendChild(createConversationEmptyState());
    if (autoScroll) stream.scrollTop = stream.scrollHeight;
    else if (newMessageAdded) showNewMessageButton();
  }

  function handleScroll() {
    const atBottom = isStreamAtBottom();
    if (atBottom !== autoScroll) {
      autoScroll = atBottom;
      if (atBottom) hideNewMessageButton();
    }
  }

  function scrollToLatestMessage() {
    autoScroll = true;
    stream.scrollTop = stream.scrollHeight;
    hideNewMessageButton();
  }

  function bind() {
    if (bound) return;
    stream?.addEventListener('scroll', handleScroll);
    newMessageButton?.addEventListener('click', scrollToLatestMessage);
    bound = true;
  }

  function destroy() {
    if (!bound) return;
    stream?.removeEventListener('scroll', handleScroll);
    newMessageButton?.removeEventListener('click', scrollToLatestMessage);
    bound = false;
  }

  return {
    bind,
    destroy,
    renderMessages,
    resetScrollState,
    showSessionLoading,
  };
}
